import { ExtraEquipmentAmenityType, UtilityRoomAmenityType } from "@instacover/helpers"

import { db } from "@/database/database"
import { predictResponseSchema } from "@/services/model/modelTypes"
import { SessionAddress, SessionDetail, SessionOrderedSteps, SessionPhotos } from "@/types/routerTypes"

import { isAmenityStepFinished } from "./amenities/isAmenityStepFinished"

type LoadServerDataToDexieParams = {
  session: NonNullable<SessionDetail>
  photos: NonNullable<SessionPhotos>
  orderedSteps: NonNullable<SessionOrderedSteps>
  address: NonNullable<SessionAddress>
}

export async function loadServerDataToDexie(params: LoadServerDataToDexieParams) {
  const { session, photos, orderedSteps, address } = params

  await db.session.upsert({
    id: session.id,
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    numberOfWalls: session.numberOfWalls,
    finishedAt: session.finishedAt,
  })

  for (const photo of photos) {
    const parsedPhotoModelResult = predictResponseSchema.safeParse(photo.modelResult)

    await db.photo.upsert({
      ...photo,
      uploadedAt: photo.updatedAt,
      uploadProgress: 101,
      modelResult: parsedPhotoModelResult.success ? parsedPhotoModelResult.data : undefined,
    })

    for (const detail of photo.instahomePhotoDetail) {
      const parsedDetailModelResult = predictResponseSchema.safeParse(detail.modelResult)

      await db.detail.upsert({
        ...detail,
        instahomeSessionId: photo.instahomeSessionId,
        uploadedAt: detail.updatedAt,
        uploadProgress: 101,
        modelResult: parsedDetailModelResult.success ? parsedDetailModelResult.data : undefined,
      })
    }
  }

  for (const amenity of session.amenities) {
    const existing = await db.amenity.get(amenity.id)

    await db.amenity.upsert({
      id: amenity.id,
      instahomeSessionId: amenity.instahomeSessionId,
      type: amenity.type,
      createdAt: amenity.createdAt,
      updatedAt: amenity.updatedAt,
      deletedAt: amenity.deletedAt,
      // If amenity is already skipped in Dexie, keep it. It usually means that local data is more fresh than server.
      skipped: existing?.skipped || amenity.skipped,
    })
  }

  const existingSteps = await db.orderedStep.findAll(session.id)
  // only add ordered steps if they don't exist yet
  // from now on, these steps are used for FE state management
  // and they assumed to be the source of truth
  if (existingSteps.length === 0) {
    const isAddressFinished = address.updatedAt > session.createdAt || photos.length > 0

    await db.orderedStep.upsert({
      name: "Intro",
      instahomeSessionId: session.id,
      createdAt: session.createdAt,
      updatedAt: session.createdAt,
      isFinished: isAddressFinished,
    })

    await db.orderedStep.upsert({
      name: "Address",
      instahomeSessionId: session.id,
      createdAt: session.createdAt,
      updatedAt: session.createdAt,
      isFinished: isAddressFinished,
    })

    const wallPhotos = await db.photo.findByType({ sessionId: session.id, type: "Wall", includeDeleted: false })

    let hasEveryWallMainPhoto = false
    for (let wallNumber = 1; wallNumber <= session.numberOfWalls; wallNumber++) {
      const photosForWall = wallPhotos.filter((photo) => photo.wallNumber === wallNumber)

      if (photosForWall.length === 0) {
        hasEveryWallMainPhoto = false
        break
      }
    }

    const utilityRoomAmenities = await db.amenity.getByAmenityGroup<UtilityRoomAmenityType>(session.id, "UtilityRoom")
    const extraEquipmentAmenities = await db.amenity.getByAmenityGroup<ExtraEquipmentAmenityType>(
      session.id,
      "ExtraEquipment",
    )

    for (const step of orderedSteps) {
      let isFinished = false

      if (step.name === "Wall") {
        isFinished = hasEveryWallMainPhoto
      } else if (step.name === "UtilityRoom") {
        isFinished = await isAmenityStepFinished({ sessionId: session.id, amenities: utilityRoomAmenities })
      } else if (step.name === "ExtraEquipment") {
        isFinished = await isAmenityStepFinished({ sessionId: session.id, amenities: extraEquipmentAmenities })
      }

      let stepMinMedia: number | undefined
      if ("minMedia" in step) {
        stepMinMedia = step.minMedia
      }

      await db.orderedStep.upsert({
        name: step.name,
        instahomeSessionId: session.id,
        minMedia: stepMinMedia,
        createdAt: session.createdAt,
        updatedAt: session.createdAt,
        ...("minDetails" in step && { minDetails: step.minDetails }),
        isFinished,
      })
    }

    await db.orderedStep.upsert({
      name: "Overview",
      instahomeSessionId: session.id,
      createdAt: session.createdAt,
      updatedAt: session.createdAt,
      isFinished: !!session.finishedAt,
    })

    await db.orderedStep.upsert({
      name: "Finished",
      instahomeSessionId: session.id,
      createdAt: session.createdAt,
      updatedAt: session.createdAt,
      isFinished: false,
    })
  }
}
